(() => {
  const selectors = {
    close: '[data-search-close]',
    drawer: '[data-search-drawer]',
    dropdown: '[data-header-dropdown]',
    input: '[data-predictive-search-input]',
    mobileMenu: '.md-header__mobile-menu',
    mobilePanel: '.md-header__mobile-panel',
    open: '[data-search-open]',
    panel: '[data-search-drawer-panel]',
  };

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  let returnFocusElement;

  function headerDropdowns() {
    return Array.from(document.querySelectorAll(selectors.dropdown));
  }

  function closeHeaderDropdowns(except = null) {
    headerDropdowns().forEach((dropdown) => {
      if (dropdown !== except) dropdown.removeAttribute('open');
    });
  }

  function initializeHeaderDropdowns() {
    const desktopQuery = window.matchMedia('(min-width: 48rem)');

    headerDropdowns().forEach((dropdown) => {
      dropdown.addEventListener('mouseenter', () => {
        if (!desktopQuery.matches) return;
        closeHeaderDropdowns(dropdown);
        dropdown.setAttribute('open', '');
      });

      dropdown.addEventListener('mouseleave', () => {
        if (desktopQuery.matches) dropdown.removeAttribute('open');
      });

      dropdown.addEventListener('focusin', () => {
        if (!desktopQuery.matches) return;
        closeHeaderDropdowns(dropdown);
        dropdown.setAttribute('open', '');
      });

      dropdown.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!dropdown.contains(document.activeElement)) {
            dropdown.removeAttribute('open');
          }
        }, 0);
      });

      dropdown.addEventListener('toggle', () => {
        if (dropdown.open) closeHeaderDropdowns(dropdown);
      });
    });
  }

  function getSearchDrawer() {
    return document.querySelector(selectors.drawer);
  }

  function closeMobileMenu() {
    const mobileMenu = document.querySelector(selectors.mobileMenu);
    if (!(mobileMenu instanceof HTMLDetailsElement)) return null;

    const menuToggle = mobileMenu.querySelector(':scope > summary');
    mobileMenu.removeAttribute('open');
    return menuToggle instanceof HTMLElement ? menuToggle : null;
  }

  function sizeMobilePanel(mobileMenu) {
    if (!(mobileMenu instanceof HTMLDetailsElement) || !mobileMenu.open) return;

    window.requestAnimationFrame(() => {
      const panel = mobileMenu.querySelector(selectors.mobilePanel);
      if (!(panel instanceof HTMLElement)) return;

      const availableHeight = Math.max(
        window.innerHeight - panel.getBoundingClientRect().top,
        240,
      );
      panel.style.height = `${availableHeight}px`;
    });
  }

  function initializeMobileMenu() {
    const mobileMenu = document.querySelector(selectors.mobileMenu);
    if (!(mobileMenu instanceof HTMLDetailsElement)) return;

    const updatePanelSize = () => sizeMobilePanel(mobileMenu);
    mobileMenu.addEventListener('toggle', updatePanelSize);
    window.addEventListener('resize', updatePanelSize);
    window.addEventListener('orientationchange', updatePanelSize);
    updatePanelSize();
  }

  function focusableElements(root) {
    return Array.from(root.querySelectorAll(focusableSelector)).filter(
      (element) => {
        return (
          element.offsetParent !== null || element === document.activeElement
        );
      },
    );
  }

  function openSearchDrawer(returnFocus = document.activeElement) {
    const drawer = getSearchDrawer();
    if (!drawer) return;

    returnFocusElement =
      returnFocus instanceof HTMLElement ? returnFocus : document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('search-drawer-open');

    const focusTarget =
      drawer.querySelector(selectors.input) ||
      drawer.querySelector(focusableSelector) ||
      drawer.querySelector(selectors.panel) ||
      drawer;
    window.setTimeout(() => focusTarget.focus(), 50);
  }

  function closeSearchDrawer(restoreFocus = true) {
    const drawer = getSearchDrawer();
    if (!drawer) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('search-drawer-open');

    if (restoreFocus && returnFocusElement?.isConnected) {
      returnFocusElement.focus();
    }
    returnFocusElement = null;
  }

  function trapSearchDrawerFocus(event) {
    if (event.key !== 'Tab') return;

    const drawer = getSearchDrawer();
    if (!drawer?.classList.contains('is-open')) return;

    const focusables = focusableElements(drawer);
    if (focusables.length === 0) {
      event.preventDefault();
      drawer.querySelector(selectors.panel)?.focus();
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    if (!event.target.closest(selectors.dropdown)) {
      closeHeaderDropdowns();
    }

    const searchButton = event.target.closest(selectors.open);
    if (searchButton) {
      event.preventDefault();
      const returnFocus = searchButton.closest(selectors.mobileMenu)
        ? closeMobileMenu()
        : searchButton;
      openSearchDrawer(returnFocus || searchButton);
      return;
    }

    if (event.target.closest(selectors.close)) {
      event.preventDefault();
      closeSearchDrawer();
    }
  });

  document.addEventListener('keydown', (event) => {
    trapSearchDrawerFocus(event);

    if (event.key === 'Escape') {
      closeSearchDrawer();

      const openDropdown = document.querySelector(`${selectors.dropdown}[open]`);
      if (openDropdown instanceof HTMLDetailsElement) {
        openDropdown.removeAttribute('open');
        openDropdown.querySelector('summary')?.focus();
      }
    }
  });

  initializeHeaderDropdowns();
  initializeMobileMenu();

  window.MedistikHeader = {
    openSearchDrawer,
    closeSearchDrawer,
  };
})();
